import { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { ALL_MONTHS, type ScheduleDefinition, type ScheduleFrequency } from '@/types';

const DAYS: { flag: number; label: string }[] = [
  { flag: 1, label: 'Mon' },
  { flag: 2, label: 'Tue' },
  { flag: 4, label: 'Wed' },
  { flag: 8, label: 'Thu' },
  { flag: 16, label: 'Fri' },
  { flag: 32, label: 'Sat' },
  { flag: 64, label: 'Sun' }
];

const WEEKDAY_FLAGS = 1 + 2 + 4 + 8 + 16;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Bit 0 is January, matching the report server's own Schedule.Month column exactly. */
const monthBit = (month1Based: number) => 1 << (month1Based - 1);

/** The mask for `count` months spaced `step` apart, starting at `anchor` (1 = January). */
function cadenceMask(anchor: number, step: number): number {
  let mask = 0;
  for (let m = anchor; m <= 12; m += step) mask |= monthBit(m);
  return mask;
}

type Cadence = 'monthly' | 'quarterly' | 'halfYearly' | 'custom';

/**
 * Which named cadence a mask represents, and the month it starts from.
 *
 * Derived from the mask rather than stored alongside it: the mask is the only thing that reaches
 * the server, so anything held separately could disagree with it, and the disagreement would show
 * up as an editor claiming "Quarterly" over a schedule that is not. An irregular set — 3140
 * (Mar/Jul/Nov/Dec) is live — simply has no cadence, and reads as Custom.
 */
function readCadence(mask: number): { cadence: Cadence; anchor: number } {
  if (mask === ALL_MONTHS) return { cadence: 'monthly', anchor: 1 };

  const months = MONTHS.map((_, i) => i + 1).filter(m => (mask & monthBit(m)) !== 0);
  const anchor = months[0] ?? 1;

  if (months.length === 4 && cadenceMask(anchor, 3) === mask && anchor <= 3)
    return { cadence: 'quarterly', anchor };
  if (months.length === 2 && cadenceMask(anchor, 6) === mask && anchor <= 6)
    return { cadence: 'halfYearly', anchor };

  return { cadence: 'custom', anchor };
}

const FREQUENCIES: { value: ScheduleFrequency; label: string }[] = [
  { value: 'Once', label: 'Once' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' }
];

const TIME_ZONES = [
  'Europe/London',
  'UTC',
  'Europe/Brussels',
  'Europe/Paris',
  'Europe/Zurich',
  'Europe/Amsterdam',
  'Africa/Nairobi',
  'America/New_York',
  'America/Toronto'
];

function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
  } catch {
    return 'Europe/London';
  }
}

/** Small debounce so the preview API isn't hit on every keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function ScheduleEditor({
  value,
  onChange
}: {
  value: ScheduleDefinition;
  onChange: (v: ScheduleDefinition) => void;
}) {
  const flags = value.daysOfWeek ?? 0;

  function toggleDay(flag: number) {
    onChange({ ...value, daysOfWeek: (flags & flag) !== 0 ? flags & ~flag : flags | flag });
  }

  function setFrequency(frequency: ScheduleFrequency) {
    const next: ScheduleDefinition = { ...value, frequency };
    // Seed sensible per-frequency defaults so switching never lands on an invalid form.
    if (frequency === 'Weekly' && !next.daysOfWeek) next.daysOfWeek = 1;
    if (frequency === 'Monthly' && !next.dayOfMonth) next.dayOfMonth = 1;
    // Seed the month set explicitly rather than leaving it null. Null does mean every month, but a
    // monthly schedule that says so outright cannot be misread by anything downstream.
    if (frequency === 'Monthly' && !next.months) next.months = ALL_MONTHS;
    if (frequency === 'Daily' && !next.intervalDays) next.intervalDays = 1;
    if (frequency === 'Once' && !next.startDate) next.startDate = toDateInput(new Date());
    onChange(next);
  }

  function applyPreset(preset: 'weekdays' | 'monday' | 'monthly1st' | 'quarterly' | 'testSoon') {
    switch (preset) {
      case 'weekdays':
        onChange({
          ...value, frequency: 'Weekly', daysOfWeek: WEEKDAY_FLAGS,
          timeOfDay: '06:30:00', startDate: null
        });
        break;
      case 'monday':
        onChange({
          ...value, frequency: 'Weekly', daysOfWeek: 1,
          timeOfDay: '09:00:00', startDate: null
        });
        break;
      case 'monthly1st':
        onChange({
          ...value, frequency: 'Monthly', dayOfMonth: 1, months: ALL_MONTHS,
          timeOfDay: '08:00:00', startDate: null
        });
        break;
      case 'quarterly':
        // Jan/Apr/Jul/Oct — the commonest quarter on the report server by a distance.
        onChange({
          ...value, frequency: 'Monthly', dayOfMonth: 1, months: cadenceMask(1, 3),
          timeOfDay: '08:00:00', startDate: null
        });
        break;
      case 'testSoon': {
        // One-off run two minutes from now in the browser's own time zone —
        // the fastest way to watch the scheduler actually fire.
        const at = new Date(Date.now() + 2 * 60_000);
        const pad = (n: number) => String(n).padStart(2, '0');
        onChange({
          ...value,
          frequency: 'Once',
          startDate: toDateInput(at),
          timeOfDay: `${pad(at.getHours())}:${pad(at.getMinutes())}:00`,
          timeZoneId: browserTimeZone()
        });
        break;
      }
    }
  }

  return (
    <div>
      <div className="field">
        <label>Quick presets</label>
        <div className="preset-chips">
          <button type="button" className="preset-chip" onClick={() => applyPreset('weekdays')}>Weekday mornings 06:30</button>
          <button type="button" className="preset-chip" onClick={() => applyPreset('monday')}>Mondays 09:00</button>
          <button type="button" className="preset-chip" onClick={() => applyPreset('monthly1st')}>1st of month 08:00</button>
          <button type="button" className="preset-chip" onClick={() => applyPreset('quarterly')}>Quarterly Jan/Apr/Jul/Oct</button>
          <button type="button" className="preset-chip preset-chip-test" onClick={() => applyPreset('testSoon')}>⚡ Test: in 2 minutes</button>
        </div>
      </div>

      <div className="field">
        <label>Frequency</label>
        <div className="day-toggles">
          {FREQUENCIES.map(f => (
            <div
              key={f.value}
              className={`day-toggle ${value.frequency === f.value ? 'on' : ''}`}
              onClick={() => setFrequency(f.value)}
            >
              {f.label}
            </div>
          ))}
        </div>
      </div>

      {value.frequency === 'Once' && (
        <div className="field">
          <label>On date</label>
          <input
            className="input"
            type="date"
            value={value.startDate ?? toDateInput(new Date())}
            onChange={e => onChange({ ...value, startDate: e.target.value || null })}
          />
        </div>
      )}

      {value.frequency === 'Daily' && (
        <div className="field">
          <label>Every</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              className="input"
              style={{ width: 90 }}
              type="number"
              min={1}
              max={90}
              value={value.intervalDays ?? 1}
              onChange={e => onChange({ ...value, intervalDays: Math.max(1, Number(e.target.value) || 1) })}
            />
            <span style={{ color: 'var(--kt-grey-500)', fontSize: '.9rem' }}>
              {(value.intervalDays ?? 1) === 1 ? 'day' : 'days'}
            </span>
          </div>
        </div>
      )}

      {value.frequency === 'Weekly' && (
        <div className="field">
          <label>On these days</label>
          <div className="day-toggles">
            {DAYS.map(d => (
              <div
                key={d.flag}
                className={`day-toggle ${(flags & d.flag) !== 0 ? 'on' : ''}`}
                onClick={() => toggleDay(d.flag)}
              >
                {d.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {value.frequency === 'Monthly' && (
        <>
          <div className="field">
            <label>Day of month</label>
            <input
              className="input"
              style={{ width: 110 }}
              type="number"
              min={1}
              max={31}
              value={value.dayOfMonth ?? 1}
              onChange={e => onChange({ ...value, dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })}
            />
          </div>
          <MonthSelector
            months={value.months ?? ALL_MONTHS}
            onChange={months => onChange({ ...value, months })}
          />
        </>
      )}

      <div className="field-row">
        <div className="field">
          <label>At time</label>
          <input
            className="input"
            type="time"
            value={value.timeOfDay.substring(0, 5)}
            onChange={e => onChange({ ...value, timeOfDay: `${e.target.value}:00` })}
          />
        </div>
        <div className="field">
          <label>Time zone</label>
          <select
            className="select"
            value={value.timeZoneId}
            onChange={e => onChange({ ...value, timeZoneId: e.target.value })}
          >
            {TIME_ZONES.includes(value.timeZoneId) ? null : <option>{value.timeZoneId}</option>}
            {TIME_ZONES.map(tz => <option key={tz}>{tz}</option>)}
          </select>
        </div>
      </div>

      {value.frequency !== 'Once' && (
        <div className="field">
          <label>Starts on <span style={{ fontWeight: 400, color: 'var(--kt-grey-400)' }}>(optional)</span></label>
          <input
            className="input"
            type="date"
            value={value.startDate ?? ''}
            onChange={e => onChange({ ...value, startDate: e.target.value || null })}
          />
        </div>
      )}

      <SchedulePreviewPanel schedule={value} />
    </div>
  );
}

/**
 * Which months a monthly schedule runs in.
 *
 * The cadence dropdowns and the twelve toggles edit the same bitmask, and the dropdowns are only
 * ever a shortcut for ticking boxes — there is no separate "quarterly" state that could drift out
 * of step with the months actually selected. That matters because the report server has no "every
 * N months" concept at all: it stores the set. A set that matches no offered cadence — Mar/Jul/Nov/
 * Dec is live, with gaps of 4, 4, 1 and 3 months — simply shows as Custom with its months ticked,
 * and stays editable a month at a time rather than being forced into a shape it does not have.
 *
 * Quarterly offers all three starting months because all three are in use: Jan/Apr/Jul/Oct on 11
 * schedules, Feb/May/Aug/Nov on 4, Mar/Jun/Sep/Dec on 2.
 */
function MonthSelector({ months, onChange }: { months: number; onChange: (months: number) => void }) {
  const { cadence, anchor } = readCadence(months);

  function setCadence(next: Cadence) {
    if (next === 'monthly') onChange(ALL_MONTHS);
    // Keep the anchor when it still makes sense for the new cadence, so switching quarterly ->
    // half-yearly from February lands on Feb/Aug rather than silently jumping back to January.
    else if (next === 'quarterly') onChange(cadenceMask(Math.min(anchor, 3), 3));
    else if (next === 'halfYearly') onChange(cadenceMask(Math.min(anchor, 6), 6));
    // 'custom' is what the toggles already produce; selecting it changes nothing.
  }

  const step = cadence === 'quarterly' ? 3 : 6;
  const anchorChoices = MONTHS.slice(0, step).map((label, i) => ({ value: i + 1, label }));

  return (
    <div className="field">
      <label>In these months</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <select
          className="select"
          style={{ width: 150 }}
          value={cadence}
          onChange={e => setCadence(e.target.value as Cadence)}
        >
          <option value="monthly">Every month</option>
          <option value="quarterly">Quarterly</option>
          <option value="halfYearly">Half-yearly</option>
          <option value="custom">Custom</option>
        </select>

        {(cadence === 'quarterly' || cadence === 'halfYearly') && (
          <>
            <span style={{ color: 'var(--kt-grey-500)', fontSize: '.9rem' }}>starting</span>
            <select
              className="select"
              style={{ width: 90 }}
              value={anchor}
              onChange={e => onChange(cadenceMask(Number(e.target.value), step))}
            >
              {anchorChoices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </>
        )}
      </div>

      <div className="month-toggles">
        {MONTHS.map((label, i) => {
          const bit = monthBit(i + 1);
          return (
            <div
              key={label}
              className={`day-toggle ${(months & bit) !== 0 ? 'on' : ''}`}
              onClick={() => onChange((months & bit) !== 0 ? months & ~bit : months | bit)}
            >
              {label}
            </div>
          );
        })}
      </div>

      {months === 0 && (
        <div className="schedule-preview-warning" style={{ marginTop: 8 }}>
          ⚠ No months selected — this schedule would never run.
        </div>
      )}
    </div>
  );
}

/** Live "when will this actually run" feedback, straight from the API's calculator. */
function SchedulePreviewPanel({ schedule }: { schedule: ScheduleDefinition }) {
  const api = useApi();
  const debounced = useDebounced(schedule, 350);

  const { data, error, isFetching } = useQuery({
    queryKey: ['schedule-preview', debounced],
    queryFn: () => api.previewSchedule({ ...debounced, humanReadable: null }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: false
  });

  return (
    <div className="schedule-preview">
      {error ? (
        <div className="schedule-preview-warning">⚠ {(error as Error).message.replace(/^API \d+: /, '')}</div>
      ) : data ? (
        <>
          <div className="schedule-preview-headline">
            {data.humanReadable}
            {isFetching && <span className="schedule-preview-updating"> · updating…</span>}
          </div>
          {data.warning && <div className="schedule-preview-warning">⚠ {data.warning}</div>}
          {data.nextRunsUtc.length > 0 && (
            <div className="schedule-preview-runs">
              <span className="schedule-preview-label">Next runs:</span>
              {data.nextRunsUtc.map(iso => (
                <span key={iso} className="schedule-preview-run">
                  {new Date(iso).toLocaleString(undefined, {
                    weekday: 'short', day: 'numeric', month: 'short',
                    hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="schedule-preview-headline">Checking schedule…</div>
      )}
    </div>
  );
}
